import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import type { BespokeFormSectionProps } from '../bespokeRegistry';
import {
  BespokeSectionProvider,
  OASISRadio,
  OASISText,
  OASISDate,
  OASISNumber,
  OASISMultiselect,
  OASISMultiselectOption,
  OASISRadioOption,
  useBespokeSectionContext,
  ControlDisabledSubtree,
} from '../primitives';
import { RepeatBindingsProvider, type RepeatBindings } from '../primitives';
import { joinClasses } from '../primitives/fieldClassSlots';
import type { FormValue } from '../../types';
import { computeLayoutDerivedTotals } from './deriveLayoutTotals';
import {
  LayoutDerivedProvider,
  useLayoutDerived,
} from './LayoutDerivedContext';
import type {
  LayoutNode,
  CardNode,
  StackNode,
  GridNode,
  IndentNode,
  TableNode,
  ConditionalNode,
  DisabledWhenNode,
  SectionLabelNode,
  DisplayNode,
  RepeatNode,
  LayoutInlineStyle,
} from './types';
import { lookupDerivedNumeric, whenClauseSatisfied } from './whenClause';

function layoutInlineStyle(
  s: LayoutInlineStyle | undefined
): CSSProperties | undefined {
  if (s == null || Object.keys(s).length === 0) return undefined;
  return s as CSSProperties;
}

// ---------------------------------------------------------------------------
// Layout helpers (same visual language as hand-coded bespoke sections)
// ---------------------------------------------------------------------------

function Card({
  node,
  answers,
  repeatBindings,
}: {
  node: CardNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  return (
    <div
      className={`space-y-6 rounded-lg border bg-card p-5 ${node.className ?? ''}`}
      style={layoutInlineStyle(node.style)}
    >
      <RenderNodes
        nodes={node.children}
        answers={answers}
        repeatBindings={repeatBindings}
      />
    </div>
  );
}

function Stack({
  node,
  answers,
  repeatBindings,
}: {
  node: StackNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  const gap = node.gap ?? 6;
  return (
    <div
      className={[`space-y-${gap}`, node.className].filter(Boolean).join(' ')}
      style={layoutInlineStyle(node.style)}
    >
      <RenderNodes
        nodes={node.children}
        answers={answers}
        repeatBindings={repeatBindings}
      />
    </div>
  );
}

function Grid({
  node,
  answers,
  repeatBindings,
}: {
  node: GridNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  const gap = node.gap ?? 6;
  return (
    <div
      className={[
        `grid grid-cols-${node.cols} gap-${gap} [&>*]:min-w-0`,
        node.className,
      ]
        .filter(Boolean)
        .join(' ')}
      style={layoutInlineStyle(node.style)}
    >
      <RenderNodes
        nodes={node.children}
        answers={answers}
        repeatBindings={repeatBindings}
      />
    </div>
  );
}

function Indent({
  node,
  answers,
  repeatBindings,
}: {
  node: IndentNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  return (
    <div className='mt-4 space-y-6 border-l-2 border-blue-200 pl-5'>
      <RenderNodes
        nodes={node.children}
        answers={answers}
        repeatBindings={repeatBindings}
      />
    </div>
  );
}

function SectionLabel({ node }: { node: SectionLabelNode }) {
  return (
    <p
      className={joinClasses(
        'text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4',
        node.className
      )}
    >
      {node.text}
    </p>
  );
}

/** Renders integers from layout `derived` (`sum`). */
function DisplayBlock({ node }: { node: DisplayNode }) {
  const derived = useLayoutDerived();
  const n = lookupDerivedNumeric(derived, node.ref);

  return (
    <div className={`text-sm tabular-nums ${node.className ?? ''}`}>
      {node.label != null && node.label !== '' ? (
        <>
          <span>{node.label}</span>
          <span className='mx-1 text-muted-foreground'>:</span>
        </>
      ) : null}
      <span className='font-medium'>{n}</span>
    </div>
  );
}

function TableRenderer({
  node,
  answers,
  repeatBindings,
}: {
  node: TableNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  return (
    <div className='overflow-x-auto'>
      <table className='w-full border-collapse text-sm'>
        <thead>
          <tr>
            <th className='border border-border bg-muted p-3 text-left font-semibold w-64' />
            {node.colHeaders.map(h => (
              <th
                key={h}
                className='border border-border bg-muted p-2 text-center text-xs font-semibold w-28'
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {node.rows.map((row, ri) => (
            <tr key={ri} className='hover:bg-muted/30 transition-colors'>
              <td className='border border-border p-3 text-sm'>{row.label}</td>
              {row.cells.map((cell, ci) => (
                <td key={ci} className='border border-border p-2 text-center'>
                  <RenderNode
                    node={cell}
                    answers={answers}
                    repeatBindings={repeatBindings}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Conditional({
  node,
  answers,
  repeatBindings,
}: {
  node: ConditionalNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  const derived = useLayoutDerived();
  if (!whenClauseSatisfied(node.when, answers, repeatBindings, derived)) {
    return node.elseChildren ? (
      <div className='space-y-6'>
        <RenderNodes
          nodes={node.elseChildren}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      </div>
    ) : null;
  }
  return (
    <div className='space-y-6'>
      <RenderNodes
        nodes={node.children}
        answers={answers}
        repeatBindings={repeatBindings}
      />
    </div>
  );
}

function DisabledWhenBlock({
  node,
  answers,
  repeatBindings,
}: {
  node: DisabledWhenNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  const derived = useLayoutDerived();
  const list = Array.isArray(node.whenAll) ? node.whenAll : [];
  const clausesPass =
    list.length === 0 ||
    list.every(w => whenClauseSatisfied(w, answers, repeatBindings, derived));
  const gateDisables = list.length > 0 && !clausesPass;
  return (
    <ControlDisabledSubtree disabled={gateDisables} className={node.className}>
      <div className='space-y-6'>
        <RenderNodes
          nodes={node.children}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      </div>
    </ControlDisabledSubtree>
  );
}

function RepeatBlock({
  node,
  answers,
  repeatBindings,
}: {
  node: RepeatNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  const { schemaMap } = useBespokeSectionContext();
  if (node.over.kind !== 'multiselectOptionsOf') {
    return null;
  }
  const question = schemaMap.get(node.over.qid);
  const rawValues = question?.values ?? [];
  const options = rawValues.map(val => ({
    value: val,
    label: question?.value_description?.[val]
      ? `${val} - ${question.value_description[val]}`
      : val,
  }));

  if (options.length === 0) {
    return (
      <div className={node.className} style={layoutInlineStyle(node.style)}>
        <p className='text-muted-foreground text-xs leading-snug'>
          Repeat has nothing to render: multiselect{' '}
          <code className='rounded bg-muted px-1 py-0.5 text-[11px] font-mono'>
            {node.over.qid}
          </code>{' '}
          is missing from the loaded section schema or has no{' '}
          <code className='rounded bg-muted px-1 py-0.5 text-[11px]'>
            values
          </code>
          . Pick the RogerDoc section that contains this question, or replace
          QIDs with ones from that slice.
        </p>
      </div>
    );
  }

  return (
    <div
      className={[`flex flex-col gap-6`, node.className]
        .filter(Boolean)
        .join(' ')}
      style={layoutInlineStyle(node.style)}
    >
      {options.map(opt => {
        const merged: RepeatBindings = { ...repeatBindings, [node.as]: opt };
        return (
          <RepeatBindingsProvider
            key={opt.value}
            parent={repeatBindings}
            merge={{ [node.as]: opt }}
          >
            <RenderNodes
              nodes={node.children}
              answers={answers}
              repeatBindings={merged}
            />
          </RepeatBindingsProvider>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Core recursive renderer
// ---------------------------------------------------------------------------

function RenderNode({
  node,
  answers,
  repeatBindings,
}: {
  node: LayoutNode;
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  switch (node.type) {
    case 'card':
      return (
        <Card node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'stack':
      return (
        <Stack node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'grid':
      return (
        <Grid node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'indent':
      return (
        <Indent node={node} answers={answers} repeatBindings={repeatBindings} />
      );
    case 'divider':
      return <div className='mt-5 pt-5 border-t' />;
    case 'static':
      return (
        <div className={node.className} style={layoutInlineStyle(node.style)}>
          {node.text}
        </div>
      );
    case 'sum':
      return null;
    case 'display':
      return <DisplayBlock node={node} />;
    case 'section-label':
      return <SectionLabel node={node} />;
    case 'table':
      return (
        <TableRenderer
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'conditional':
      return (
        <Conditional
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'disabled-when':
      return (
        <DisabledWhenBlock
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );
    case 'repeat':
      return (
        <RepeatBlock
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      );

    case 'OASISRadio':
      return (
        <OASISRadio
          qid={node.qid}
          hideLabel={node.hideLabel}
          inline={node.inline}
          className={node.className}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          groupClassName={node.groupClassName}
          orientation={node.orientation}
        />
      );
    case 'OASISText':
      return (
        <OASISText
          qid={node.qid}
          multiline={node.multiline}
          hideLabel={node.hideLabel}
          className={node.className}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISDate':
      return (
        <OASISDate
          qid={node.qid}
          hideLabel={node.hideLabel}
          className={node.className}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISNumber':
      return (
        <OASISNumber
          qid={node.qid}
          hideLabel={node.hideLabel}
          className={node.className}
          innerClassName={node.innerClassName}
          labelClassName={node.labelClassName}
          inputClassName={node.inputClassName}
        />
      );
    case 'OASISMultiselect': {
      const hasCompound = node.children != null && node.children.length > 0;
      return (
        <OASISMultiselect
          qid={node.qid}
          hideLabel={node.hideLabel}
          className={node.className}
          innerClassName={node.innerClassName}
          checklistClassName={node.checklistClassName}
          checklistStyle={layoutInlineStyle(node.checklistStyle)}
          compoundContent={
            hasCompound ? (
              <RenderNodes
                nodes={node.children!}
                answers={answers}
                repeatBindings={repeatBindings}
              />
            ) : undefined
          }
        />
      );
    }
    case 'OASISMultiselectOption':
      return (
        <OASISMultiselectOption
          valueFrom={node.valueFrom}
          className={node.className}
          labelClassName={node.labelClassName}
        />
      );
    case 'radio-option':
      return (
        <OASISRadioOption
          qid={node.qid}
          value={node.value}
          className={node.className}
        />
      );
  }
}

function RenderNodes({
  nodes,
  answers,
  repeatBindings,
}: {
  nodes: LayoutNode[];
  answers: Record<string, FormValue>;
  repeatBindings: RepeatBindings;
}) {
  return (
    <>
      {nodes.map((node, i) => (
        <RenderNode
          key={i}
          node={node}
          answers={answers}
          repeatBindings={repeatBindings}
        />
      ))}
    </>
  );
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export interface BespokeLayoutRendererProps extends BespokeFormSectionProps {
  layout: LayoutNode[];
}

export function BespokeLayoutRenderer({
  layout,
  ...props
}: BespokeLayoutRendererProps) {
  // useMemo must come before any conditional return (Rules of Hooks).
  const derivedMap = useMemo(
    () =>
      Array.isArray(layout)
        ? computeLayoutDerivedTotals(layout, props.answers)
        : {},
    [layout, props.answers]
  );

  if (!Array.isArray(layout)) {
    return (
      <div className='rounded border border-destructive p-4 text-sm text-destructive'>
        Layout error: layout_json must be a JSON array of nodes.
      </div>
    );
  }

  return (
    <BespokeSectionProvider {...props}>
      <LayoutDerivedProvider value={derivedMap}>
        <div className='space-y-8'>
          <RenderNodes
            nodes={layout}
            answers={props.answers}
            repeatBindings={{}}
          />
        </div>
      </LayoutDerivedProvider>
    </BespokeSectionProvider>
  );
}
